import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getPublicPosts } from "../services/api/blog";
import { BlogPost } from "../types";
import { BASE_URL } from "../services/api/client";
import { Calendar, ArrowRight, Eye, Tag, Loader2 } from "lucide-react";

export const NewsSection: React.FC = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await getPublicPosts(1, 6); // Fetch latest 6 posts
        const mappedPosts: BlogPost[] = response.data.map((api) => ({
          id: api.id,
          title: api.title,
          slug: api.slug,
          excerpt: api.excerpt || "",
          category: api.categories?.[0]?.name || "Umum",
          categories: api.categories || [],
          tags: api.tags || [],
          medias: api.medias || [],
          content: api.content || "",
          seoTitle: api.seoTitle || api.title,
          seoDescription: api.seoDescription || api.metaDescription || "",
          seoKeywords: api.seoKeywords || api.keywords || "",
          status: (api.status === "published" ? "Published" : "Draft") as any,
          date: api.publishedAt || "",
          views: (api.postView as any)?.view || 0,
          author: api.author?.fullName || "Admin",
        }));
        setPosts(mappedPosts);
      } catch (error) {
        console.error("Failed to fetch news:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPosts();
  }, []);

  const handleOpenPost = (post: BlogPost) => {
    navigate(`/news/${post.slug}`);
  };

  if (isLoading) {
    return (
      <div className="py-24 flex justify-center items-center">
        <Loader2 className="w-8 h-8 animate-spin text-giz-primary" />
      </div>
    );
  }

  if (posts.length === 0) return null;

  return (
    <section id="news" className="py-16 bg-white relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-giz-primary font-bold tracking-wider uppercase text-sm">
            Update Terkini
          </span>
          <h2 className="text-3xl font-bold text-gray-900 mt-2">
            Kabar Wonderland
          </h2>
          <p className="text-gray-500 mt-2">
            Berita, event, dan tips seru seputar GIZ Park & RIW.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {posts.map((post) => (
            <div
              key={post.id}
              className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col group h-full"
            >
              <div className="h-48 bg-gray-100 relative overflow-hidden">
                {post.medias && post.medias.length > 0 ? (
                  <img
                    src={`${post.medias[0].fileUrl}`}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                ) : (
                  <div
                    className={`w-full h-full bg-gradient-to-br ${
                      post.category === "Promo"
                        ? "from-red-100 to-orange-100"
                        : post.category === "Edukasi"
                        ? "from-green-100 to-emerald-100"
                        : "from-indigo-100 to-blue-100"
                    } flex items-center justify-center`}
                  >
                    <span className="text-4xl opacity-20 font-bold tracking-widest uppercase">
                      {post.category}
                    </span>
                  </div>
                )}
                <div className="absolute top-4 left-4 flex gap-2">
                  {post.categories.slice(0, 2).map((cat) => (
                    <span
                      key={cat.id}
                      className="bg-white/90 backdrop-blur-sm text-gray-800 text-[10px] font-bold px-2 py-1 rounded-full shadow-sm flex items-center gap-1"
                    >
                      <Tag className="w-2 h-2" /> {cat.name}
                    </span>
                  ))}
                </div>
              </div>

              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-center gap-4 text-xs text-gray-400 mb-3">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />{" "}
                    {new Date(post.date).toLocaleDateString("id-ID", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </span>
                  <span className="flex items-center gap-1">
                    <Eye className="w-3 h-3" /> {post.views}
                  </span>
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-giz-primary transition-colors">
                  {post.title}
                </h3>

                <p className="text-gray-600 text-sm line-clamp-3 mb-4 flex-1">
                  {post.excerpt ||
                    post.content.replace(/<[^>]*>?/gm, "").substring(0, 100) +
                      "..."}
                </p>

                <button
                  onClick={() => handleOpenPost(post)}
                  className="text-giz-primary font-bold text-sm flex items-center gap-2 mt-auto group-hover:gap-3 transition-all"
                >
                  Baca Selengkapnya <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
